'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  QrCode,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  ScanLine,
  ChevronLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useHardwareScanner } from '@/hooks/use-hardware-scanner';
import {
  useVerifyQrMutation,
  useVerifyQrByNumberMutation
} from '../services/reception.mutations';
import type { Shipment, ReceptionSession } from '../types';

interface StepQrScanProps {
  shipment?: Shipment | null;
  session: ReceptionSession | null;
  onVerified?: (session: ReceptionSession) => void;
  onComplete: (session: ReceptionSession) => void;
}

export function StepQrScan({
  shipment,
  session,
  onVerified,
  onComplete
}: StepQrScanProps) {
  const t = useTranslations('reception');
  const [shipmentNumber, setShipmentNumber] = useState(
    shipment?.shipment_number ?? ''
  );
  const verifyQrMutation = useVerifyQrMutation();
  const verifyQrByNumberMutation = useVerifyQrByNumberMutation();

  const isVerified = !!session?.qr_verified_at;
  const isDiscoveryMode = !shipment;

  // ─── Handlers ──────────────────────────────────────────────────────────

  const isPending =
    verifyQrMutation.isPending || verifyQrByNumberMutation.isPending;
  const isError = verifyQrMutation.isError || verifyQrByNumberMutation.isError;

  const handleVerify = useCallback(
    async (overrideNumber?: string) => {
      const targetNumber = (overrideNumber ?? shipmentNumber).trim();
      if (isDiscoveryMode && !targetNumber) return;

      verifyQrMutation.reset();
      verifyQrByNumberMutation.reset();

      try {
        if (isDiscoveryMode) {
          const result =
            await verifyQrByNumberMutation.mutateAsync(targetNumber);
          if (result?.data) {
            onVerified?.(result.data);
          }
        } else {
          const result = await verifyQrMutation.mutateAsync(shipment!.id);
          if (result?.data) {
            onVerified?.(result.data);
          }
        }
      } catch {
        // Mutation error state (isError) will show the error banner
      }
    },
    [
      isDiscoveryMode,
      shipmentNumber,
      verifyQrMutation,
      verifyQrByNumberMutation,
      shipment,
      onVerified
    ]
  );

  // Keep a stable ref so useHardwareScanner callback doesn't re-create when shipmentNumber changes
  const handleVerifyRef = useRef(handleVerify);
  useEffect(() => {
    handleVerifyRef.current = handleVerify;
  }, [handleVerify]);

  const handleRetry = () => {
    verifyQrMutation.reset();
    verifyQrByNumberMutation.reset();
  };

  // ─── Hardware Scanner ──────────────────────────────────────────────────

  useHardwareScanner({
    onScan: (value) => {
      if (isDiscoveryMode) {
        setShipmentNumber(value);
        handleVerifyRef.current(value);
      } else {
        handleVerifyRef.current();
      }
    },
    enabled: !isVerified && !isPending
  });

  // ─── Verified state — show shipment details ────────────────────────────

  if (isVerified) {
    const displayShipment = shipment ?? session?.shipment;
    return (
      <Card className='container border-0 shadow-sm'>
        <CardContent className='p-6 sm:p-8'>
          {/* Header: Title + subtitle with QR icon */}
          <div className='mb-6 flex items-start justify-between'>
            <div className='flex-1 text-end'>
              <h2 className='text-xl font-bold'>{t('scanShipmentQrTitle')}</h2>
              <p className='text-muted-foreground mt-1 text-sm'>
                {t('scanShipmentQrSubtitle')}
              </p>
            </div>
            <div className='bg-primary/10 ms-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl'>
              <QrCode className='text-primary h-6 w-6' />
            </div>
          </div>

          {/* Success banner */}
          <div
            className={cn(
              'mb-6 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-green-700',
              'dark:border-green-800 dark:bg-green-950 dark:text-green-300'
            )}
          >
            <CheckCircle2 className='h-5 w-5 shrink-0' />
            <div>
              <p className='text-sm font-medium'>{t('qrVerifiedSuccess')}</p>
              <p className='text-xs opacity-80'>{t('qrVerifiedSubtitle')}</p>
            </div>
          </div>

          {/* Shipment details — vertical list */}
          {displayShipment && (
            <div className='divide-y rounded-lg border'>
              <div className='flex items-center justify-between px-4 py-3'>
                <span className='font-medium'>
                  {displayShipment.shipment_number}
                </span>
                <span className='text-muted-foreground text-sm'>
                  {t('shipmentNumber')}
                </span>
              </div>
              <div className='flex items-center justify-between px-4 py-3'>
                <span className='font-medium'>
                  {displayShipment.driver?.name ?? '—'}
                </span>
                <span className='text-muted-foreground text-sm'>
                  {t('driver')}
                </span>
              </div>
              {/* <div className='flex items-center justify-between px-4 py-3'>
                <span className='font-medium'>
                  {displayShipment.box_count} {t('boxesSuffix')}
                </span>
                <span className='text-muted-foreground text-sm'>
                  {t('expectedBoxes')}
                </span>
              </div> */}
              <div className='flex items-center justify-between px-4 py-3'>
                <div className='flex items-center gap-2'>
                  <span className='font-medium'>
                    {displayShipment.shipment_date ?? t('notAvailable')}
                  </span>
                  {displayShipment.is_future_arrival && (
                    <span className='rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'>
                      {t('arrivalFutureFlag')}
                    </span>
                  )}
                </div>
                <span className='text-muted-foreground text-sm'>
                  {t('arrivalDate')}
                </span>
              </div>
            </div>
          )}

          {/* Continue button */}
          <div className='mt-6 flex justify-start'>
            <Button onClick={() => session && onComplete(session)}>
              {t('continueToBoxCount')}
              <ChevronLeft className='ms-2 h-4 w-4 ltr:rotate-180' />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Scan state — hardware scanner + manual input ──────────────────────

  return (
    <Card className='container border-0 shadow-sm'>
      <CardContent className='p-6 sm:p-8'>
        {/* Header: Title + subtitle with QR icon */}
        <div className='mb-6 flex items-start justify-between'>
          <div className='flex-1 text-end'>
            <h2 className='text-xl font-bold'>{t('scanShipmentQrTitle')}</h2>
            <p className='text-muted-foreground mt-1 text-sm'>
              {t('scanShipmentQrSubtitle')}
            </p>
          </div>
          <div className='bg-primary/10 ms-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl'>
            <QrCode className='text-primary h-6 w-6' />
          </div>
        </div>

        {/* Hardware scanner waiting indicator */}
        <div className='mb-6 flex h-32 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-900/30'>
          <span className='relative flex h-3 w-3'>
            <span
              className={cn(
                'absolute inline-flex h-full w-full rounded-full opacity-75',
                isPending ? 'bg-gray-400' : 'animate-ping bg-green-400'
              )}
            />
            <span
              className={cn(
                'relative inline-flex h-3 w-3 rounded-full',
                isPending ? 'bg-gray-400' : 'bg-green-500'
              )}
            />
          </span>
          <p className='text-muted-foreground text-sm'>{t('waitingForScan')}</p>
        </div>

        {/* Error banner */}
        {isError && (
          <div
            className={cn(
              'mb-4 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-3 text-red-700',
              'dark:border-red-800 dark:bg-red-950 dark:text-red-300'
            )}
          >
            <div className='flex items-center gap-2'>
              <AlertCircle className='h-5 w-5 shrink-0' />
              <span className='text-sm font-medium'>
                {t('qrVerificationFailed')}
              </span>
            </div>
            <Button variant='ghost' size='sm' onClick={handleRetry}>
              <RotateCcw className='me-1 h-4 w-4' />
              {t('retryScan')}
            </Button>
          </div>
        )}

        {/* Input + Verify button row */}
        <div className='flex flex-col gap-3 sm:flex-row'>
          <Button
            className='order-2 sm:order-1'
            onClick={() => handleVerify()}
            disabled={!shipmentNumber.trim() || isPending}
          >
            <ScanLine className='me-2 h-4 w-4' />
            {isPending ? t('verifying') : t('scanAndVerify')}
          </Button>
          <div className='relative order-1 flex-1 sm:order-2'>
            <Input
              id='shipment-number'
              value={shipmentNumber}
              onChange={(e) => setShipmentNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleVerify();
              }}
              placeholder={t('enterShipmentNumber')}
              className='pe-10'
              readOnly={!isDiscoveryMode}
            />
            <ScanLine className='text-muted-foreground pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2' />
          </div>
        </div>

        {/* Continue button (disabled) */}
        <div className='mt-6 flex justify-start'>
          <Button variant='outline' disabled>
            {t('continueToBoxCount')}
            <ChevronLeft className='ms-2 h-4 w-4 ltr:rotate-180' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
